import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { find } from "lodash";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import * as Yup from "yup";
import AlertMessage from "../../../../../components/AlertMessage";
import FormProvider from "../../../../../components/forms/FormProvider";
import Input from "../../../../../components/forms/Input";
import { useBillingPlans } from "../../../../../context/billingPlans";
import { useMembers } from "../../../../../context/members";
import { useWorkspaces } from "../../../../../context/workspaces";
import { PaymentStep } from "../../../../../utils/types";

export interface Props {
  open: boolean;
  toggleAddMemberForm: () => void;
}

const AddMemberFormSidebar: React.FC<Props> = ({
  open,
  toggleAddMemberForm
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { members, inviteMember, membersLimitExceeded } = useMembers();
  const { currentWorkspace } = useWorkspaces();
  const { currentPlan, setPaymentStep } = useBillingPlans();

  const onSubmit = useCallback(
    async ({ email }: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      setErrorMessage(null);

      const apiResponse = await inviteMember([email]);

      if (!apiResponse?.status) {
        setErrorMessage(apiResponse?.message || "Something went wrong");
      } else {
        toggleAddMemberForm();
      }

      formikHelpers.setSubmitting(false);
    },
    [inviteMember, toggleAddMemberForm]
  );

  const validateEmailField = useCallback(
    async (email: string) => {
      const memberIsAlreadyInvited = !!find(
        members.filter(({ deleted_at }) => !deleted_at),
        {
          email
        }
      );

      if (memberIsAlreadyInvited) {
        return "This person was already invited to your workspace.";
      }
    },
    [members]
  );

  const limitExceededAlertMessage = useMemo(() => {
    if (!membersLimitExceeded || !currentWorkspace) {
      return null;
    }

    const { allowed_team_members } = currentWorkspace.active_plan;

    return (
      <AlertMessage
        type="warning"
        title={
          <span>
            This workspace is on the {currentPlan?.name} which is limited to{" "}
            {allowed_team_members + 1} members. You are trying to add a new
            member resulting in {members.length + 1} members which is not
            allowed. Please upgrade your plan{" "}
            <NavLink
              className="font-bold text-hotspotty-300"
              onClick={() => {
                setPaymentStep(PaymentStep.plans);
                toggleAddMemberForm();
              }}
              to={"/pricing"}
            >
              here
            </NavLink>{" "}
            or remove members.
          </span>
        }
      />
    );
  }, [
    currentPlan?.name,
    currentWorkspace,
    members.length,
    membersLimitExceeded,
    setPaymentStep,
    toggleAddMemberForm
  ]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={open}
        onClose={toggleAddMemberForm}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-md">
                <FormProvider
                  classnames={{ wrapper: "h-full" }}
                  onCancel={toggleAddMemberForm}
                  error={errorMessage}
                  disabled={membersLimitExceeded}
                  formikProps={{
                    onSubmit,
                    initialValues: { email: "" },
                    validationSchema: Yup.object().shape({
                      email: Yup.string()
                        .email("Please enter a valid email address")
                        .required("Required")
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                  submitButtonText="Send invitation"
                  submittingSubmitButtonText="Sending invitation"
                >
                  {({ isSubmitting }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium text-white">
                              New member
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={toggleAddMemberForm}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <Field
                                id="search_member"
                                name="email"
                                label="Email"
                                placeholder="john@acme.com"
                                type="email"
                                component={Input}
                                disabled={isSubmitting || membersLimitExceeded}
                                validate={validateEmailField}
                              />
                            </div>

                            {limitExceededAlertMessage}
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddMemberFormSidebar;

//
// Utils
//

interface FormValues {
  email: string;
}

const ORDERED_FIELD_NAMES = ["email"];
